/**
 * React hook for managing page-lock subscription data.
 * Subscribes to real-time page lock/release events (onLockPage/onReleasePageLock)
 * via AppSync and invalidates the affected page/deck caches so the UI reflects
 * page-lock changes made by other users.
 *
 */

import { onLockPage, onReleasePageLock } from '@services/api/deckManagement/graphql/schema/subscriptions';

import { decksQueryKeys } from './useAllDecks';
import { deckQueryKeys } from './useDeck';
import { pageQueryKeys } from './usePage';
import { createSubscriptionHook, SubscriptionHookName } from './useSubscriptionFactory';

/**
 * Interface for Page Subscription parameters
 * @param deckId - The deck whose page-lock events to react to (scopes the subscription lifecycle)
 * @param enabled - Whether the subscription should be made active
 * @interface
 */
export interface UsePageLockSubscriptionParams {
  deckId: string;
  enabled?: boolean;
}

/**
 * Invalidate the page-grain queries for the scoped deck (to refresh lock attributes on the page
 * records) plus the deck/deck-list queries. The subscription is deck-scoped and doesn't know which
 * page changed, so it invalidates ALL getPagesByPageName variants via the `pageGrains` prefix.
 *
 * Note: we intentionally do NOT invalidate `pageQueryKeys.all` here — since that prefix also matches the
 * decklines query (`['page','decklines',...]`), which is configured with staleTime/gcTime 0 and would
 * result in a refetch + rebuild of the current page's grid on every lock event (and discard unsaved deckline
 * edits). A lock only changes lock attributes on the page-grain records.
 */
const getPageLockInvalidateKeys = (deckId: string) => [
  pageQueryKeys.pageGrainsAll(),
  pageQueryKeys.lockedPagesForDeck(deckId),
  deckQueryKeys.deck(deckId),
  decksQueryKeys.list(),
];

/**
 * Creates a subscription hook for page-lock events (lock, release).
 */
const pageLockSubscriptionHook = createSubscriptionHook(SubscriptionHookName.PageLockSubscription, [
  {
    query: onLockPage,
    eventType: 'onLockPage',
    getInvalidateKeys: getPageLockInvalidateKeys,
  },
  {
    query: onReleasePageLock,
    eventType: 'onReleasePageLock',
    getInvalidateKeys: getPageLockInvalidateKeys,
  },
]);

/**
 * Hook for subscribing to page-lock events to support real-time page-level locking.
 * @param deckId - The deck to scope the subscription to
 * @param enabled - Whether the subscription should be made active
 */
export const usePageLockSubscription = ({ deckId, enabled = false }: UsePageLockSubscriptionParams): void => {
  pageLockSubscriptionHook({ enabled: enabled && !!deckId, scopeKey: deckId });
};
